import {
  type CustomWorkbookChapters,
  type CustomWorkbookCreateBody,
  type CustomWorkbookDetail,
  type CustomWorkbookTreeNode,
  type CustomWorkbookType,
  type CustomWorkbookUpdateBody,
  type GroupingChapterType,
  type InnerDesignTemplateBody,
  makeCustomWorkbookApi,
  type RecommendationBody,
} from '@mathflat/domain/MakeCustomWorkbook'
import {
  type CustomSignatureWorkbook,
  type CustomSignatureWorkbookBody,
  type CustomWorkbook,
  type SignatureWorkbookDraft,
  type SignatureWorkbookDraftBody,
  type Workbook,
  workbookApi,
  type WorkbookFileParams,
  type WorkbookPageWithProblems,
} from '@mathflat/domain/Workbook'

import { certifiedWorkbookList, type InspectWorkbook } from '@/api/certifiedWorkbookList'
import { mpEventInfo } from '@/constant'
import { WorkbookPage, WorkbookPageColl, WorkbookPageProblem, WorkbookPageProblemColl } from '@/dto'
import { FlatTree } from '@/models/FlatTree'
import type { RawMakeProblemT } from '@/page/MakeCustomWorkbook/context/abstract/baseMakeCustomWorkbookContext'
import type { RawMakeChapterT } from '@/page/MakeCustomWorkbook/context/abstract/baseMakeCustomWorkbookContext'
import { createLatestRequestSignal } from '@/utils/axios/latestRequestSignal'
import type { CustomRequestConfigMeta } from '@/utils/axios/maxios'

import { makeAdapter } from './Adapter'

export class WorkbookController {
  private signalFor = createLatestRequestSignal('workbook/custom')

  adapter = {
    workbook: {
      fromSupportedWorkbookPageList({ pageProblems, ...response }: WorkbookPageWithProblems) {
        return new WorkbookPage({
          ...response,
          pageProblemColl: new WorkbookPageProblemColl(
            pageProblems.map((p) => new WorkbookPageProblem(p)),
          ),
        })
      },
    },
    inspectWorkbookList: makeAdapter({
      from(data: InspectWorkbook[]) {
        return data.map(({ bookId }) => {
          return Number(bookId)
        })
      },
    }),
    customWorkbookRequest: {
      /**
       * Custom Workbook 만들기 트리에 보관된 응답 객체를 POST 요청 스펙 형태로 변환.
       * `chapters[].children[]` 노드에서 요청에 포함되지 않는 필드(`sourceType` 등)를 제거.
       */
      to<Body extends { chapters: unknown }>(payload: Body): Body {
        const convertNode = (node: unknown): unknown => {
          if (!node || typeof node !== 'object') return node
          const { sourceType, ...rest } = node as Record<string, unknown> & { sourceType?: unknown }
          void sourceType
          const children = (rest as { children?: unknown }).children
          if (Array.isArray(children)) {
            return { ...rest, children: children.map(convertNode) }
          }
          return rest
        }
        const chapters = payload.chapters as unknown
        if (!Array.isArray(chapters)) return payload
        return {
          ...payload,
          chapters: chapters.map(convertNode),
        } as Body
      },
    },
    chapterProblems: makeAdapter({
      from(data: { chapters: CustomWorkbookChapters }) {
        const processChildren = (
          item: CustomWorkbookTreeNode,
        ): RawMakeProblemT | RawMakeChapterT => {
          if (!('children' in item) && item.type === 'PROBLEM') {
            return FlatTree.treeNode.fromRawDataToRawTreeNode(item, 'id')
          }

          if (item.children) {
            return FlatTree.treeNode.fromRawDataToRawTreeNode({
              ...item,
              children: (item.children as CustomWorkbookTreeNode[]).map(processChildren),
            }) as RawMakeChapterT
          }

          throw new Error('Invalid item')
        }
        return (data.chapters as CustomWorkbookTreeNode[]).map((c) => processChildren(c))
      },
    }),
  }

  customWorkbook = {
    getProblemCount: async <T extends CustomWorkbookType>(
      payload: RecommendationBody<T>,
      meta?: CustomRequestConfigMeta,
    ) =>
      makeCustomWorkbookApi.getRecommendedProblemCount(payload, { signal: this.signalFor(meta) }),
    getChapterProblemsByFilter: async <T extends CustomWorkbookType = CustomWorkbookType>(
      payload: RecommendationBody<T>,
      meta?: CustomRequestConfigMeta,
    ) => {
      const chapters = await makeCustomWorkbookApi.getRecommendedProblems(payload, {
        signal: this.signalFor(meta),
      })
      return this.adapter.chapterProblems.from({ chapters })
    },
    getChapterProblemsByWorkbookId: async (
      workbookId: CustomWorkbookDetail['id'],
      shape: Parameters<typeof makeCustomWorkbookApi.getCustomWorkbookChapters>[1],
    ) => {
      const chapters = await makeCustomWorkbookApi.getCustomWorkbookChapters(workbookId, shape)
      return this.adapter.chapterProblems.from({ chapters })
    },
    getWorkbookById: async (
      workbookId: CustomWorkbookDetail['id'],
      meta?: CustomRequestConfigMeta,
    ) =>
      makeCustomWorkbookApi.getCustomWorkbookDetail(workbookId, {
        signal: this.signalFor({ useAbortController: true, ...meta }),
      }),
    postWorkbook: async <
      T extends CustomWorkbookType,
      G extends GroupingChapterType = GroupingChapterType,
    >(
      payload: CustomWorkbookCreateBody<T, G>,
    ) => makeCustomWorkbookApi.createCustomWorkbook(this.adapter.customWorkbookRequest.to(payload)),
    putWorkbook: async <
      T extends CustomWorkbookType,
      G extends GroupingChapterType = GroupingChapterType,
    >(
      workbookId: CustomWorkbookDetail['id'],
      payload: CustomWorkbookUpdateBody<T, G>,
    ) =>
      makeCustomWorkbookApi.updateCustomWorkbook(
        workbookId,
        this.adapter.customWorkbookRequest.to(payload),
      ),
    getInnerDesignTemplate: async <T extends CustomWorkbookType>(
      payload: InnerDesignTemplateBody<T>,
    ) => makeCustomWorkbookApi.getInnerDesignTemplates(payload),

    deleteWorkbooks: async (workbookIds: CustomWorkbook['id'][]) =>
      workbookApi.deleteCustomWorkbooks(workbookIds, { mp_event_name: mpEventInfo.교재삭제 }),
  }

  supportedWorkbook = {
    fetchWorkbookProblemPageList: async (workbookId: Workbook['id']) => {
      const pages = await workbookApi.getWorkbookPagesWithProblems(workbookId, {
        mp_event_name: mpEventInfo.교재페이지조회,
      })

      return new WorkbookPageColl(pages.map(this.adapter.workbook.fromSupportedWorkbookPageList))
    },
  }

  fetchAssignedStudentList = async (workbookId: Workbook['id']) =>
    workbookApi.getWorkbookAssignedStudents(workbookId)

  customSignatureWorkbook = {
    deleteSignatureWorkbook: async (workbookId: CustomSignatureWorkbook['id']) => {
      await workbookApi.deleteCustomSignatureWorkbook(workbookId)
    },
    restoreWorkbookCustomSignature: async (id: CustomSignatureWorkbook['id']) =>
      workbookApi.restoreCustomSignatureWorkbook(id),
    postFilter: async (payload: SignatureWorkbookDraftBody) => {
      const formData = new FormData()
      Object.entries(payload).forEach(([key, value]) => {
        if (value) {
          formData.append(key, typeof value === 'number' ? `${value}` : value)
        }
      })
      return workbookApi.createSignatureWorkbookDraft(formData)
    },
    getFilterByFilterId: async (filterId: SignatureWorkbookDraft['filterId']) =>
      workbookApi.getSignatureWorkbookDraft(filterId),
    postCustomSignature: async (payload: CustomSignatureWorkbookBody) => ({
      workbookId: await workbookApi.createCustomSignatureWorkbook(payload),
    }),
    putCustomSignature: async (
      id: CustomSignatureWorkbook['id'],
      payload: CustomSignatureWorkbookBody,
    ) => ({ workbookId: await workbookApi.updateCustomSignatureWorkbook(id, payload) }),
  }

  fetchWorkbookDownload = async (params: WorkbookFileParams) =>
    workbookApi.getWorkbookFile(params, { mp_event_name: mpEventInfo.교재다운로드 })
  fetchInspectWorkbookList = async () => {
    const { data } = await certifiedWorkbookList.getInspectWorkbooks()
    return this.adapter.inspectWorkbookList.from(data)
  }
}

export const workbookController = new WorkbookController()
